import { z } from "zod";
import { reviewActionSchema, reviewStateSchema } from "./core/review";

export { reviewStateSchema } from "./core/review";
export const idParams = z.object({ id: z.uuid() });
export const optionalIdQuery = z.object({ id: z.string().optional() });
export const apiErrorSchema = z.object({
  error: z.string(),
  code: z.string().optional(),
  retryable: z.boolean().optional(),
  retryAfter: z.number().optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
export const okSchema = z.object({ ok: z.boolean() });
/** Per-id outcome for an action applied across a selection of records. */
export const bulkActionResultSchema = z.object({
  succeeded: z.array(z.string()),
  failed: z.array(z.object({ id: z.string(), error: z.string() })),
});
export type BulkActionResult = z.infer<typeof bulkActionResultSchema>;
export const bulkIdsSchema = z.array(z.uuid()).min(1).max(100);
export const jsonValueSchema = z.json();
export const queryResultSchema = z.object({
  columns: z.array(z.string()),
  rows: z.array(z.array(z.unknown())),
  truncated: z.boolean(),
  elapsedMs: z.number(),
});
export const searchQuery = z.object({
  q: z.string().optional(),
  page: z.string().optional(),
  days: z.string().optional(),
});
export const recordKeySchema = z.string().regex(/^(person|observation):[a-f0-9-]{36}$/);
export const reviewStatesSchema = z.record(z.string(), z.object({ state: reviewStateSchema }));
export const MAX_BULK_RECORDS = 500;
export const recordSelectionSchema = z
  .array(z.object({ id: z.string(), key: recordKeySchema }))
  .max(MAX_BULK_RECORDS);
export const reviewInputSchema = z.object({
  keys: z.array(recordKeySchema).min(1).max(MAX_BULK_RECORDS),
  action: reviewActionSchema,
});
export const reviewHistorySchema = z.array(
  z.object({
    id: z.string(),
    previousState: z.string().nullable(),
    state: z.string(),
    action: z.string(),
    createdAt: z.string(),
    createdBy: z.string(),
    createdByName: z.string().nullable(),
    actorUserId: z.string().nullable(),
    actorName: z.string().nullable(),
  }),
);
export type ReviewHistoryEntry = z.infer<typeof reviewHistorySchema>[number];
export const integrationSchema = z.object({
  id: z.string(),
  provider: z.string(),
  name: z.string(),
  account: z.string(),
  enabled: z.boolean(),
  isDefault: z.boolean(),
  checkedAt: z.string().nullable().optional(),
});
export const integrationStateSchema = z.object({
  id: z.string(),
  enabled: z.boolean(),
});
export const savedQuerySchema = z.object({
  id: z.string(),
  name: z.string(),
  query: z.string(),
  revision: z.number(),
});
export const queryRevisionSchema = z.object({
  revision: z.number().int().positive(),
});
export const personRecordKeySchema = z
  .string()
  .regex(/^person:[a-f0-9-]{36}$/, "Deliveries only support people.");
export const sendInputSchema = z.object({
  integrationId: z.string().min(1),
  keys: z.array(recordKeySchema).min(1).max(MAX_BULK_RECORDS),
  includeSignalPayload: z.boolean().optional(),
});
export const signalKeysSchema = z
  .array(z.string().regex(/^observation:[a-f0-9-]{36}$/))
  .min(1)
  .max(MAX_BULK_RECORDS);
export const sendResultSchema = z.object({
  queued: z.number(),
  existing: z.number(),
  destination: z.string(),
  states: reviewStatesSchema,
});
export const destinationSchema = z.object({
  id: z.string(),
  name: z.string(),
  provider: z.enum(["slack", "webhook"]),
});
